// Exakte Ganzzahl-Arithmetik und deutsche Zahleneingabe – Grundlage für money.ts und qty.ts.
// Keine Gleitkomma-Rechnung: Zwischenergebnisse laufen über BigInt, Ergebnisse sind sichere Ganzzahlen.

export type RoundingMode = 'half-up' | 'half-even' | 'floor' | 'ceil' | 'trunc'

const abs = (v: bigint): bigint => (v < 0n ? -v : v)

/**
 * Ganzzahl-Division mit Rundung. `half-up` ist die kaufmännische Rundung:
 * bei genau der Hälfte vom Nullpunkt weg (2,5 → 3 und −2,5 → −3).
 */
export function divRound(numerator: bigint, denominator: bigint, mode: RoundingMode = 'half-up'): bigint {
  if (denominator === 0n) throw new RangeError('Division durch null')
  const quotient = numerator / denominator
  const remainder = numerator % denominator
  if (remainder === 0n) return quotient
  const sign = numerator < 0n !== denominator < 0n ? -1n : 1n
  const twice = abs(remainder) * 2n
  const den = abs(denominator)
  switch (mode) {
    case 'half-up':
      return twice >= den ? quotient + sign : quotient
    case 'half-even':
      if (twice > den) return quotient + sign
      if (twice === den) return quotient % 2n === 0n ? quotient : quotient + sign
      return quotient
    case 'floor':
      return sign < 0n ? quotient - 1n : quotient
    case 'ceil':
      return sign > 0n ? quotient + 1n : quotient
    case 'trunc':
      return quotient
  }
}

export function assertSafeInt(value: number, label = 'Wert'): number {
  if (!Number.isSafeInteger(value)) throw new RangeError(`${label} muss eine sichere Ganzzahl sein, erhalten: ${value}`)
  return value
}

export function toSafeNumber(value: bigint, label = 'Wert'): number {
  const n = Number(value)
  if (!Number.isSafeInteger(n)) throw new RangeError(`${label} liegt außerhalb des sicheren Ganzzahlbereichs: ${value}`)
  return n
}

/** `a × b ÷ divisor`, exakt gerechnet und einmal gerundet. Alle Argumente sind Ganzzahlen. */
export function mulDiv(a: number, b: number, divisor: number, mode: RoundingMode = 'half-up'): number {
  assertSafeInt(a, 'Faktor')
  assertSafeInt(b, 'Faktor')
  assertSafeInt(divisor, 'Divisor')
  return toSafeNumber(divRound(BigInt(a) * BigInt(b), BigInt(divisor), mode))
}

/** Kaufmännisch gerundete Ganzzahl aus einer Dezimalzahl (nur für Eingaben/Importe, nie für Summen). */
export function roundHalfUp(value: number): number {
  if (!Number.isFinite(value)) throw new RangeError(`Keine endliche Zahl: ${value}`)
  // Über die Dezimaldarstellung runden, damit 1.005 * 100 nicht zu 100 wird.
  const [mantissa = '0', exponent = '0'] = value.toExponential(15).split('e')
  const negative = mantissa.startsWith('-')
  const digits = mantissa.replace('-', '').replace('.', '')
  const shift = Number(exponent) - 15 // Wert = digits × 10^shift
  let scaled: bigint
  if (shift >= 0) scaled = BigInt(digits) * 10n ** BigInt(shift)
  else scaled = divRound(BigInt(digits), 10n ** BigInt(-shift), 'half-up')
  return toSafeNumber(negative ? -scaled : scaled)
}

// ---- Deutsche Zahleneingabe (design-system §6.4) ------------------------------

const pow10 = (n: number): bigint => 10n ** BigInt(n)

function parseLiteral(literal: string, scale: number): bigint | null {
  let intPart: string
  let fraction = ''
  if (literal.includes(',')) {
    const parts = literal.split(',')
    if (parts.length !== 2) return null
    intPart = parts[0] ?? ''
    fraction = parts[1] ?? ''
    if (intPart.includes('.')) {
      if (!/^\d{1,3}(\.\d{3})+$/.test(intPart)) return null
      intPart = intPart.replaceAll('.', '')
    }
  } else if (literal.includes('.')) {
    // Punkt = Tausenderpunkt, wenn genau drei Ziffern folgen (1.500 = 1500), sonst Dezimalpunkt (16.9 = 16,9).
    if (/^[1-9]\d{0,2}(\.\d{3})+$/.test(literal)) {
      intPart = literal.replaceAll('.', '')
    } else {
      const parts = literal.split('.')
      if (parts.length !== 2) return null
      intPart = parts[0] ?? ''
      fraction = parts[1] ?? ''
    }
  } else {
    intPart = literal
  }
  if (intPart === '' && fraction === '') return null
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fraction)) return null
  const whole = BigInt(intPart === '' ? '0' : intPart) * pow10(scale)
  if (fraction === '') return whole
  const fractionScaled =
    fraction.length <= scale
      ? BigInt(fraction) * pow10(scale - fraction.length)
      : divRound(BigInt(fraction), pow10(fraction.length - scale), 'half-up')
  return whole + fractionScaled
}

/**
 * Liest eine Eingabe in deutscher Schreibweise als skalierte Ganzzahl (`scale` Nachkommastellen):
 * `parseDecimalDe('1.234,50 €', 2)` → 123450. Komma ist Dezimaltrennzeichen; Leerzeichen, €, Einheiten
 * am Ende werden ignoriert; Minus als `-` oder `−`. Einfache Ausdrücke werden ausgerechnet:
 * `12*20`, `480-2`, `3+2` (Punkt vor Strich). Ungültiges ergibt `null`.
 */
export function parseDecimalDe(text: string, scale: number): number | null {
  let s = text
    .replaceAll('−', '-')
    .replaceAll('×', '*')
    .replace(/[\s  ]/g, '')
    .replace(/€|EUR/gi, '')
  s = s.replace(/[a-zäöüß%]+\.?$/i, '') // Einheit am Ende („Ka“, „kg“, „St.“)
  if (s === '') return null

  // Zerlegen in Zahlen und Operatoren; ein Minus direkt nach Operator oder am Anfang ist ein Vorzeichen.
  const tokens: (bigint | '+' | '-' | '*')[] = []
  let i = 0
  while (i < s.length) {
    const ch = s[i] as string
    const prev = tokens[tokens.length - 1]
    const expectsNumber = prev === undefined || typeof prev === 'string'
    if (expectsNumber) {
      let sign = 1n
      if (ch === '-' || ch === '+') {
        sign = ch === '-' ? -1n : 1n
        i++
      }
      let j = i
      while (j < s.length && /[\d.,]/.test(s[j] as string)) j++
      if (j === i) return null
      const value = parseLiteral(s.slice(i, j), scale)
      if (value === null) return null
      tokens.push(sign * value)
      i = j
    } else {
      if (ch !== '+' && ch !== '-' && ch !== '*') return null
      tokens.push(ch)
      i++
    }
  }
  if (typeof tokens[tokens.length - 1] !== 'bigint') return null

  // Punkt vor Strich
  const terms: bigint[] = []
  let current = tokens[0] as bigint
  let pendingSign = 1n
  for (let k = 1; k < tokens.length; k += 2) {
    const op = tokens[k] as '+' | '-' | '*'
    const operand = tokens[k + 1] as bigint
    if (op === '*') {
      current = divRound(current * operand, pow10(scale), 'half-up')
    } else {
      terms.push(pendingSign * current)
      pendingSign = op === '-' ? -1n : 1n
      current = operand
    }
  }
  terms.push(pendingSign * current)
  const total = terms.reduce((acc, t) => acc + t, 0n)
  const result = Number(total)
  return Number.isSafeInteger(result) ? result : null
}

/** Skalierte Ganzzahl als Ziffernfolge mit Dezimalpunkt (ohne Gruppierung): (123450, 2) → '1234.50'. */
export function scaledToDecimalString(value: number, scale: number): string {
  assertSafeInt(value)
  const negative = value < 0
  const digits = String(Math.abs(value)).padStart(scale + 1, '0')
  const whole = digits.slice(0, digits.length - scale)
  const fraction = scale > 0 ? `.${digits.slice(-scale)}` : ''
  return `${negative ? '-' : ''}${whole}${fraction}`
}
